import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

export interface LogLine {
  ts: string;
  severity: 'error' | 'warn' | 'info' | 'debug';
  message: string;
  component?: string;
  meta?: Record<string, unknown>;
  id: string | number;
}

const LABEL: Record<LogLine['severity'], string> = {
  error: 'ERRO',
  warn: 'WARN',
  info: 'INFO',
  debug: 'DEBG',
};

const SEVERITY_COLOR: Record<LogLine['severity'], string> = {
  error: 'text-accent-red',
  warn: 'text-accent-orange',
  info: 'text-text-secondary',
  debug: 'text-text-dimmed',
};

export function LogStream({ lines, filter }: { lines: LogLine[]; filter?: string }) {
  const [tail, setTail] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tail && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [lines, tail]);

  const norm = filter?.toLowerCase() ?? '';

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-md border border-border bg-bg-primary">
      <div className="flex h-9 items-center justify-between border-b border-border px-3 text-sm">
        <div className="font-medium text-text-secondary">Log</div>
        <button
          type="button"
          onClick={() => setTail((t) => !t)}
          className={clsx(
            'inline-flex items-center gap-1.5 rounded border px-2 py-0.5 font-mono text-xs transition-colors duration-100',
            tail
              ? 'border-accent-green/40 bg-accent-green/10 text-accent-green'
              : 'border-border bg-bg-tertiary text-text-secondary hover:text-text-primary',
          )}
        >
          <span>↓</span>
          <span>{tail ? 'Live' : 'Paused'}</span>
        </button>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overflow-x-hidden font-mono text-[13px] leading-relaxed"
      >
        {lines.map((line) => {
          const matched = norm && line.message.toLowerCase().includes(norm);
          return (
            <div
              key={line.id}
              className={clsx(
                'flex gap-3 px-3 py-0.5 animate-logFlash',
                matched && 'bg-accent-yellow/20',
              )}
            >
              <span className="w-36 shrink-0 text-text-dimmed">{line.ts}</span>
              <span className={clsx('w-12 shrink-0 font-medium', SEVERITY_COLOR[line.severity])}>
                {LABEL[line.severity]}
              </span>
              <span className={clsx('flex-1 min-w-0', SEVERITY_COLOR[line.severity])}>
                {line.component && (
                  <span className="mr-1.5 rounded px-1 py-0.5 text-[10px] font-semibold" style={{ background: 'rgba(13,207,184,0.12)', color: 'var(--accent-blue)' }}>
                    {line.component}
                  </span>
                )}
                {line.message}
                {line.meta && Object.keys(line.meta).length > 0 && (
                  <span className="ml-2 text-text-dimmed text-[10px]">
                    {Object.entries(line.meta)
                      .filter(([, v]) => v !== null && v !== undefined)
                      .map(([k, v]) => `${k}=${String(v)}`)
                      .join(' ')}
                  </span>
                )}
              </span>
            </div>
          );
        })}
        {lines.length === 0 && (
          <div className="px-3 py-4 text-center font-sans text-sm italic text-text-dimmed">
            No log events yet.
          </div>
        )}
      </div>
    </div>
  );
}
